import http from '@/plugins/api'
import { startLoading } from '@/plugins/loading'
import * as types from './types'

// BMS Processing Utility Functions
const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0
    var v = c == 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

const generateRandomRoNumber = () => {
  return Math.floor(Math.random() * 9000000000) + 1000000000
}

const addInsuranceCompanyToXml = (xmlContent, insurance) => {
  // Handle different data structures (search results vs static list)
  const companyName = insurance.name || insurance.insuranceName || 'Unknown Insurance'
  const companyId = insurance.firm || insurance.insurance || insurance.insuranceId || ''
  
  const insuranceXml = `
    <InsuranceCompany>
        <Party>
            <OrgInfo>
                <CompanyName>${companyName}</CompanyName>
                <IDInfo>
                    <IDQualifierCode>CompanyID</IDQualifierCode>
                    <IDNum>${companyId}</IDNum>
                </IDInfo>
            </OrgInfo>
        </Party>
    </InsuranceCompany>`
  
  // Check if AdminInfo section exists
  if (xmlContent.includes('<AdminInfo>')) {
    // Check if InsuranceCompany already exists
    if (xmlContent.includes('<InsuranceCompany>')) {
      // Replace existing InsuranceCompany section
      return xmlContent.replace(
        /<InsuranceCompany>[\s\S]*?<\/InsuranceCompany>/g,
        insuranceXml.trim()
      )
    } else {
      // Add InsuranceCompany as first child of AdminInfo
      return xmlContent.replace(
        /<AdminInfo>/,
        `<AdminInfo>${insuranceXml}`
      )
    }
  } else {
    // Create AdminInfo section with InsuranceCompany
    const adminInfoXml = `
<AdminInfo>${insuranceXml}
</AdminInfo>`
    
    // Insert AdminInfo before EstimatorIDs or at the end before closing tag
    if (xmlContent.includes('<EstimatorIDs>')) {
      return xmlContent.replace(
        /<EstimatorIDs>/,
        `${adminInfoXml}
<EstimatorIDs>`
      )
    } else {
      return xmlContent.replace(
        /<\/VehicleDamageEstimateAddRq>/,
        `${adminInfoXml}
</VehicleDamageEstimateAddRq>`
      )
    }
  }
}

const applyFieldModifications = (xmlContent, customVin, newRqUID, newDocumentID, newRepairOrderID, generatedValues) => {
  let modifiedContent = xmlContent
  
  // Replace RqUID
  modifiedContent = modifiedContent.replace(
    /<RqUID>([^<]*)<\/RqUID>/g,
    `<RqUID>${newRqUID}</RqUID>`
  )
  
  // Replace DocumentID
  modifiedContent = modifiedContent.replace(
    /<DocumentID>([^<]*)<\/DocumentID>/g,
    `<DocumentID>${newDocumentID}</DocumentID>`
  )
  
  // Parse estimator name into first and last name
  const estimatorName = (generatedValues && generatedValues.estimatorName) || 'TestUser TestLastName'
  const nameParts = estimatorName.split(' ')
  const firstName = nameParts[0] || 'TestUser'
  const lastName = nameParts.slice(1).join(' ') || 'TestLastName'
  
  // Replace PersonName fields
  modifiedContent = modifiedContent.replace(
    /<FirstName>([^<]*)<\/FirstName>/g,
    `<FirstName>${firstName}</FirstName>`
  )
  modifiedContent = modifiedContent.replace(
    /<LastName>([^<]*)<\/LastName>/g,
    `<LastName>${lastName}</LastName>`
  )
  
  // Replace CommEmail
  const estimatorEmail = (generatedValues && generatedValues.estimatorEmail) || 'ADAS@opusivs.com'
  modifiedContent = modifiedContent.replace(
    /<CommEmail>([^<]*)<\/CommEmail>/g,
    `<CommEmail>${estimatorEmail}</CommEmail>`
  )
  
  // Replace CompanyName (but not inside InsuranceCompany sections)
  // First temporarily protect InsuranceCompany sections
  const insuranceCompanyPattern = /<InsuranceCompany>[\s\S]*?<\/InsuranceCompany>/g
  const insuranceCompanySections = modifiedContent.match(insuranceCompanyPattern) || []
  const tempPlaceholders = insuranceCompanySections.map((_, index) => `__INSURANCE_COMPANY_${index}__`)
  
  // Replace insurance company sections with placeholders
  insuranceCompanySections.forEach((section, index) => {
    modifiedContent = modifiedContent.replace(section, tempPlaceholders[index])
  })
  
  // Now safely replace CompanyName outside of InsuranceCompany sections
  const companyName = (generatedValues && generatedValues.companyName) || 'Opus IVS'
  modifiedContent = modifiedContent.replace(
    /<CompanyName>([^<]*)<\/CompanyName>/g,
    `<CompanyName>${companyName}</CompanyName>`
  )
  
  // Restore insurance company sections
  tempPlaceholders.forEach((placeholder, index) => {
    modifiedContent = modifiedContent.replace(placeholder, insuranceCompanySections[index])
  })
  
  // Replace Address1
  modifiedContent = modifiedContent.replace(
    /<Address1>([^<]*)<\/Address1>/g,
    '<Address1>123 Test Street</Address1>'
  )
  
  // Replace City
  modifiedContent = modifiedContent.replace(
    /<City>([^<]*)<\/City>/g,
    '<City>Test City</City>'
  )
  
  // Replace CommPhone
  modifiedContent = modifiedContent.replace(
    /<CommPhone>([^<]*)<\/CommPhone>/g,
    '<CommPhone>555-123-4567</CommPhone>'
  )
  
  // Replace CurrentEstimatorID
  modifiedContent = modifiedContent.replace(
    /<CurrentEstimatorID>([^<]*)<\/CurrentEstimatorID>/g,
    `<CurrentEstimatorID>${estimatorName}</CurrentEstimatorID>`
  )
  
  // Replace RepairOrderID
  modifiedContent = modifiedContent.replace(
    /<RepairOrderID>([^<]*)<\/RepairOrderID>/g,
    `<RepairOrderID>${newRepairOrderID}</RepairOrderID>`
  )
  
  // Replace VIN if custom VIN is provided
  if (customVin) {
    modifiedContent = modifiedContent.replace(
      /<VINNum>([^<]*)<\/VINNum>/g,
      `<VINNum>${customVin}</VINNum>`
    )
  }
  
  // Replace vehicle information if provided
  if (generatedValues) {
    // Replace vehicle year
    if (generatedValues.vehicleYear) {
      modifiedContent = modifiedContent.replace(
        /<ModelYear>([^<]*)<\/ModelYear>/g,
        `<ModelYear>${generatedValues.vehicleYear}</ModelYear>`
      )
    }
    
    // Replace vehicle make - need to get the name from the lookup value
    if (generatedValues.vehicleMake) {
      // For now, use the value directly - ideally we'd lookup the name
      modifiedContent = modifiedContent.replace(
        /<Manufacturer>([^<]*)<\/Manufacturer>/g,
        `<Manufacturer>${generatedValues.vehicleMake}</Manufacturer>`
      )
    }
    
    // Replace vehicle model
    if (generatedValues.vehicleModel) {
      modifiedContent = modifiedContent.replace(
        /<Model>([^<]*)<\/Model>/g,
        `<Model>${generatedValues.vehicleModel}</Model>`
      )
    }
    
    // Replace CCC Secureshare ID if provided
    if (generatedValues.cccSecureshare) {
      // Replace RepairFacility IDNum (most common location for CCC Secureshare)
      modifiedContent = modifiedContent.replace(
        /(<RepairFacility>[\s\S]*?<IDInfo>[\s\S]*?<IDNum>)([^<]*)(<\/IDNum>)/g,
        `$1${generatedValues.cccSecureshare}$3`
      )
    }
    
    // Replace Insurance Company information if provided
    if (generatedValues.insuranceCompanyName || generatedValues.insuranceCompanyCode) {
      // Replace insurance company name
      if (generatedValues.insuranceCompanyName) {
        modifiedContent = modifiedContent.replace(
          /(<InsuranceCompany>[\s\S]*?<CompanyName>)([^<]*)(<\/CompanyName>)/g,
          `$1${generatedValues.insuranceCompanyName}$3`
        )
      }
      
      // Replace insurance company code/ID
      if (generatedValues.insuranceCompanyCode) {
        modifiedContent = modifiedContent.replace(
          /(<InsuranceCompany>[\s\S]*?<IDInfo>[\s\S]*?<IDNum>)([^<]*)(<\/IDNum>)/g,
          `$1${generatedValues.insuranceCompanyCode}$3`
        )
      }
    }
  }
  
  return modifiedContent
}

// Utility function to extract vehicle data from BMS XML
const extractVehicleDataFromXml = (xmlContent) => {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlContent, 'text/xml')
  
  // Extract VIN
  const vinElement = xmlDoc.querySelector('VINNum')
  const vin = vinElement ? vinElement.textContent.trim() : ''
  
  // Extract Year
  const yearElement = xmlDoc.querySelector('ModelYear')
  const year = yearElement ? parseInt(yearElement.textContent.trim()) : new Date().getFullYear()
  
  // Extract Make
  const makeElement = xmlDoc.querySelector('MakeDesc')
  const make = makeElement ? makeElement.textContent.trim() : ''
  
  // Extract Model
  const modelElement = xmlDoc.querySelector('ModelName')
  const model = modelElement ? modelElement.textContent.trim() : ''
  
  // Extract estimator information
  const firstNameElement = xmlDoc.querySelector('Estimator PersonName FirstName')
  const lastNameElement = xmlDoc.querySelector('Estimator PersonName LastName')
  const firstName = firstNameElement ? firstNameElement.textContent.trim() : 'TestUser'
  const lastName = lastNameElement ? lastNameElement.textContent.trim() : 'TestLastName'
  const estimatorName = `${firstName} ${lastName}`
  
  // Extract estimator email
  const emailElement = xmlDoc.querySelector('Estimator Communications CommEmail')
  const estimatorEmail = emailElement ? emailElement.textContent.trim() : 'ADAS@opusivs.com'
  
  // Extract company name
  const companyElement = xmlDoc.querySelector('RepairFacility CompanyName')
  const companyName = companyElement ? companyElement.textContent.trim() : 'Opus IVS'
  
  // Extract RqUID, DocumentID, RepairOrderID for reference (though we'll generate new ones)
  const rqUIDElement = xmlDoc.querySelector('RqUID')
  const documentIDElement = xmlDoc.querySelector('DocumentID')
  const repairOrderIDElement = xmlDoc.querySelector('RepairOrderID')
  
  // Extract CCC Secureshare number - this might be in various formats in the XML
  // Common locations could be in EstimatorID, CompanyID, or custom fields
  let cccSecureshare = ''
  
  // Try to find CCC ID in various possible locations
  // Look for RepairFacility IDNum (most common location for CCC Secureshare ID)
  const cccIdElement = xmlDoc.querySelector('RepairFacility Party OrgInfo IDInfo IDNum') ||
                     xmlDoc.querySelector('RepairFacility IDInfo IDNum') ||
                     xmlDoc.querySelector('EstimatorID[IDQualifierCode="CCC"] IDNum') ||
                     xmlDoc.querySelector('IDInfo[IDQualifierCode="CCC"] IDNum') ||
                     xmlDoc.querySelector('CompanyID[IDQualifierCode="CCC"] IDNum') ||
                     xmlDoc.querySelector('CCCId') ||
                     xmlDoc.querySelector('CccId')
  
  if (cccIdElement) {
    cccSecureshare = cccIdElement.textContent.trim()
  }
  
  // Extract insurance company information
  const insuranceCompanyNameElement = xmlDoc.querySelector('InsuranceCompany Party OrgInfo CompanyName')
  const insuranceCompanyName = insuranceCompanyNameElement ? insuranceCompanyNameElement.textContent.trim() : ''
  
  const insuranceCompanyCodeElement = xmlDoc.querySelector('InsuranceCompany Party OrgInfo IDInfo IDNum')
  const insuranceCompanyCode = insuranceCompanyCodeElement ? insuranceCompanyCodeElement.textContent.trim() : ''
  
  return {
    vin,
    vehicleYear: year,
    vehicleMake: make,
    vehicleModel: model,
    estimatorName,
    estimatorEmail,
    companyName,
    cccSecureshare,
    insuranceCompanyName,
    insuranceCompanyCode,
    // Include original IDs for reference (will be replaced with new generated ones)
    originalRqUID: rqUIDElement ? rqUIDElement.textContent.trim() : '',
    originalDocumentID: documentIDElement ? documentIDElement.textContent.trim() : '',
    originalRepairOrderID: repairOrderIDElement ? repairOrderIDElement.textContent.trim() : ''
  }
}

export default {
  setSearchTerm({ commit }, text) {
    commit(types.SET_SEARCH_TERM, text)
  },
  setPageSize({ commit }, pageSize) {
    commit(types.SET_PAGE_SIZE, pageSize)
  },
  setSortDir({ commit }, sortDir) {
    commit(types.SET_SORT_DIR, sortDir)
  },
  setOrderBy({ commit }, orderBy) {
    commit(types.SET_ORDER_BY, orderBy)
  },
  setFilterBy({ commit }, filterBy) {
    commit(types.SET_FILTER_BY, filterBy)
  },
  setFilterOptions({ commit }, data) {
    commit(types.SET_FILTER_OPTIONS, data)
  },
  initAdas({ commit }, data) {
    commit(types.INIT_ADAS, data)
  },
  initAdasInspection({ commit }, data) {
    commit(types.INIT_ADASINSPECTION, data)
  },
  updateAdas({ commit }, data) {
    commit(types.UPDATE_ADAS, data)
  },
  updateAdasInspection({ commit }, data) {
    commit(types.UPDATE_ADASINSPECTION, data)
  },
  resetRequests({ commit }) {
    commit(types.RESET_REQUESTS)
  },
  resetRequest({ commit }) {
    commit(types.RESET_REQUEST)
  },
  setRequest({ commit }, data) {
    commit(types.SET_REQUEST, data)
  },
  async getAdass({ commit, dispatch }, searchOptions) {
    const r = await startLoading(dispatch, 'getAdass', () =>
      http.get('adas/adass', {
        params: searchOptions
      })
    )
    commit(types.SET_ADASS, r.data)
    return r.data
  },
  async getAdas({ commit, dispatch }, id) {
    if (id === undefined || id === null) {
      return
    }
    const r = await startLoading(dispatch, 'getAdas', () => http.get(`adas/adas?adas=${id}`))
    commit(types.SET_ADAS, r.data)
    return r.data
  },
  async createAdas({ commit, state, dispatch }, data) {
    const r = await startLoading(dispatch, 'createAdas', () => http.post('adas/createAdas', data))
    return r.data
  },
  async saveAdas({ commit, state, dispatch }, data) {
    const r = await startLoading(dispatch, 'saveAdas', () => http.post('adas/saveAdas', data))
    return r.data
  },
  async saveAdasInspection({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'saveAdasInspection', () => http.post('adas/saveAdasInspection', data))
    commit(types.UPDATE_ADAS_INSPECTION_RECORD, data)
    return r.data
  },
  async getAdasInspections({ commit, dispatch }, id) {
    const r = await startLoading(dispatch, 'getAdasInspections', () => http.get(`adas/adasInspections?adas=${id}`))
    commit(types.SET_ADASINSPECTIONS, r.data)
    return r.data
  },
  async getAdasInspection({ commit, dispatch }, id) {
    const r = await startLoading(dispatch, 'getAdasInspection', () => http.get(`adas/adasInspection?adasInspection=${id}`))
    commit(types.SET_ADASINSPECTION, r.data)
    return r.data
  },
  async deleteAdasInspection({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'deleteAdasInspection', () => http.post('adas/deleteInspection', data))
    return r.data
  },
  async getAdasInspectionItems({ commit, dispatch }, id) {
    const r = await startLoading(dispatch, 'getAdasInspectionItems', () => http.get(`adas/adasInspectionItems?adasInspection=${id}`))
    commit(types.SET_ADASINSPECTIONITEMS, r.data)
    return r.data
  },
  async getAdasInspectionResults({ commit, dispatch }, id) {
    const r = await startLoading(dispatch, 'getAdasInspectionResults', () => http.get(`adas/adasInspectionResults?adasInspection=${id}`))
    commit(types.SET_ADASINSPECTIONRESULTS, r.data)
    return r.data
  },
  async saveAdasInspectionResults({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'saveAdasInspectionResults', () => http.post('adas/saveAdasInspectionResults', data))
    return r.data
  },
  async sendReport({ commit, dispatch }, report) {
    const r = await startLoading(dispatch, 'sendReport', () => http.get(`report/send${report.report}?adasInspection=${report.adasInspection}`))
    return r.data
  },
  async getReportPreference({ commit, dispatch }, adasInspection) {
    const r = await startLoading(dispatch, 'getReportPreference', () => http.get(`report/reportPreference?adasInspection=${adasInspection}`))
    commit(types.SET_REPORTPREFERENCE, r.data)
    return r.data
  },
  async getRepairLines({ commit, dispatch }, adas) {
    const r = await startLoading(dispatch, 'getRepairLines', () => http.get(`adas/repairLines?adas=${adas}`))
    commit(types.SET_REPAIRLINES, r.data)
    return r.data
  },
  async updateStatusAdas({ commit, dispatch }, status) {
    const r = await startLoading(dispatch, 'updateStatusAdas', () => http.post('adas/updateStatusAdas', status))
    commit(types.SET_ADAS_STATUS, status)
    return r.data
  },
  async updateStatusAdasInspection({ commit, dispatch }, status) {
    const r = await startLoading(dispatch, 'updateStatusAdasInspection', () => http.post('adas/updateStatusAdasInspection', status))
    commit(types.SET_ADAS_INSPECTION_STATUS, status)
    return r.data
  },
  async resetDecodedAdas({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'resetDecodedAdas', () => http.post('adas/decode', data))
    commit(types.SET_ADAS_RESET, r.data)
    return r.data
  },
  async getLinks({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'getLinks', () =>
      http.get('alldata/getLinksInternal', {
        params: data
      })
    )
    commit(types.SET_LINKS, r.data)
    return r.data
  },
  async getGeneralInfoLinks({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'getLinks', () =>
      http.get('alldata/getGeneralInfoLinks', {
        params: data
      })
    )
    commit(types.SET_LINKS, r.data)
    return r.data
  },
  async getAdasChecklistMappedToDecoded({ commit, dispatch }, id) {
    const r = await startLoading(dispatch, 'getAdasChecklistMappedToDecoded', () => http.get(`admin/getAdasChecklistMappedToDecoded?id=${id}`))
    commit(types.SET_DECODED, r.data)
    return r.data
  },
  async scrubData({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'scrubData', () => http.get(`bms/scrub?adas=${data.adas}&adasInspection=${data.adasInspection}`))
    return r.data
  },
  async pushToCCC({ commit, dispatch }, adasInspection) {
    const r = await startLoading(dispatch, 'pushToCCC', () => http.get(`adas/pushToCCC?adasInspection=${adasInspection}`))
    return r.data
  },
  async pushToCalCopilot({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'pushToCalCopilot', () => http.post('adas/createCalibrate', data))
    return r.data
  },
  async saveEquipped({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'saveEquipped', () => http.post('adas/saveEquipped', data))
    commit(types.SET_EQUIPPED, data)
    return r.data
  },
  async neoadasDecode({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'neoadasDecode', () => http.post('decode/neoadas', data))
    return r.data
  },
  async saveResultEquipped({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'saveResultEquipped', () => http.post('adas/saveResultEquipped', data))
    commit(types.SET_RESULT_EQUIPPED, data)
    return r.data
  },
  async getRequests({ commit, dispatch }, adasInspection) {
    const r = await startLoading(dispatch, 'getRequests', () => http.get(`adas/request?adasInspection=${adasInspection}`))
    commit(types.SET_REQUESTS, r.data)
    return r.data
  },
  async deleteRequest({ commit, dispatch }, id) {
    const r = await startLoading(dispatch, 'deleteRequest', () => http.get(`adas/deleteRequest?calibrateRequest=${id}`))
    return r.data
  },
  async getRequestNotes({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'getRequestNotes', () =>
      http.get(`adas/requestNotes?calibrateRequest=${data.calibrateRequest}&readBy=${data.readBy}`)
    )
    commit(types.SET_REQUEST_NOTES, r.data)
    return r.data
  },
  async deleteRequestNote({ commit, dispatch }, id) {
    const r = await startLoading(dispatch, 'deleteRequestNote', () => http.get(`adas/deleteRequestNote?calibrateRequestNote=${id}`))
    return r.data
  },
  async saveRequestNote({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'saveRequestNote', () => http.post('adas/saveRequestNote', data))
    return r.data
  },
  async saveRequestStatus({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'saveRequestStatus', () => http.post('adas/saveRequestStatus', data))
    return r.data
  },
  async getAdasInspectionReview({ commit, dispatch }, { adasInspection, providerLaunchPadId }) {
    const r = await startLoading(dispatch, 'getAdasInspectionReview', () =>
      http.get(`adas/adasInspectionReview?adasInspection=${adasInspection}&providerLaunchPadId=${providerLaunchPadId || ''}`)
    )
    console.log('adasInspectionReview', r.data)
    return r.data
  },
  async saveAdasInspectionReview({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'saveAdasInspectionReview', () =>
      http.post('adas/saveAdasInspectionReview', data)
    )
    console.log('saveAdasInspectionReview response', r.data)
    return r.data
  },
  async getAdasInspectionNotInstalled({ commit, dispatch }, id) {
    const r = await startLoading(dispatch, 'notInstalledOnInspection', () => http.get(`adas/notInstalledOnInspection?adasInspection=${id}`))
    commit(types.SET_ADASINSPECTION_NOT_INSTALLED, r.data)
    return r.data
  },

  // BMS File Processing Actions
  async uploadBmsFile({ dispatch }, { file, customVin, selectedInsurance, generatedValues }) {
    try {
      console.log('Starting BMS file upload...', { fileName: file.name, customVin, selectedInsurance, generatedValues })
      
      const fileContent = await dispatch('readFileAsText', file)
      console.log('File content length:', fileContent.length)
      console.log('File content preview:', fileContent.substring(0, 200))
      
      const modifiedContent = await dispatch('modifyBmsFields', { xmlContent: fileContent, customVin, selectedInsurance, generatedValues })
      console.log('Modified content length:', modifiedContent.length)
      console.log('Modified content preview:', modifiedContent.substring(0, 200))
      
      const response = await startLoading(dispatch, 'uploadBmsFile', () => 
        http.post('bms/estimate', modifiedContent, {
          headers: {
            'Content-Type': 'application/xml'
          }
        })
      )
      
      return {
        success: true,
        data: response.data,
        fileName: file.name,
        customVin,
        insuranceId: selectedInsurance ? (selectedInsurance.firm || selectedInsurance.insurance) : null,
        insuranceName: selectedInsurance ? (selectedInsurance.name || selectedInsurance.insuranceName) : ''
      }
    } catch (error) {
      console.error('BMS upload error:', error)
      return {
        success: false,
        error: error.response && error.response.data || error.message
      }
    }
  },

  readFileAsText(context, file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = e => resolve(e.target.result)
      reader.onerror = reject
      reader.readAsText(file)
    })
  },

  extractVehicleData(context, xmlContent) {
    return extractVehicleDataFromXml(xmlContent)
  },

  modifyBmsFields({ dispatch }, { xmlContent, customVin, selectedInsurance, generatedValues }) {
    console.log('modifyBmsFields called with:', { 
      xmlContentLength: xmlContent.length, 
      customVin, 
      selectedInsurance,
      generatedValues,
      xmlPreview: xmlContent.substring(0, 100)
    })
    
    // Use provided values or generate new ones as fallback
    const newRqUID = (generatedValues && generatedValues.rqUID) || generateGuid()
    const newDocumentID = (generatedValues && generatedValues.documentID) || generateGuid()
    const newRepairOrderID = (generatedValues && generatedValues.repairOrderID) || generateRandomRoNumber()
    
    console.log('Using IDs:', { newRqUID, newDocumentID, newRepairOrderID })
    
    // Call the utility functions for transformations
    let modifiedContent = xmlContent
    
    // Add insurance company FIRST (before other replacements)
    if (selectedInsurance) {
      console.log('Adding insurance company:', selectedInsurance)
      modifiedContent = addInsuranceCompanyToXml(modifiedContent, selectedInsurance)
      console.log('After adding insurance, content length:', modifiedContent.length)
    }
    
    // Apply all the field modifications
    console.log('Applying field modifications...')
    modifiedContent = applyFieldModifications(modifiedContent, customVin, newRqUID, newDocumentID, newRepairOrderID, generatedValues)
    console.log('After field modifications, content length:', modifiedContent.length)
    
    console.log('Final modified content preview:', modifiedContent.substring(0, 200))
    
    return modifiedContent
  },
  async getAdasInspectionNotInstalled({ commit, dispatch }, id) {
    const r = await startLoading(dispatch, 'notInstalledOnInspection', () => http.get(`adas/notInstalledOnInspection?adasInspection=${id}`))
    commit(types.SET_ADASINSPECTION_NOT_INSTALLED, r.data)
    return r.data
  },
  async removeSystemFromInspection({ commit, dispatch }, dataPacket) {
    // console.log('Removing Data Packet: ', dataPacket)
    const r = await startLoading(dispatch, 'removeSystemFromInspection', () => http.post('adas/removeSystemFromInspection', dataPacket))
    return r.data
  },
  async addSystemToInspection({ commit, dispatch }, data) {
    // console.log('Add Matrix Item: ', data)
    const r = await startLoading(dispatch, 'addSystemToInspection', () => http.post('adas/addSystemToInspection', data))
    return r.data
  },
  async getCalibrationSubOptionsForMatrix({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'getCalibrationSubOptionsForMatrix', () => http.post('adas/getCalibrationSubOptionsForMatrix', data))
    return r.data
  },
  async saveSubcategoryOnAdasInspectionResult({ commit, dispatch }, data) {
    const r = await startLoading(dispatch, 'saveAdasInspectionResultSubCategory', () => http.post('adas/saveAdasInspectionResultSubCategory', data))
    return r.data
  }
  

}



// WEBPACK FOOTER //
// ./src/store/modules/adas/actions.js